// src/components/WeatherWidget.tsx
// Widget de clima compacto. Usa Open-Meteo (gratis, sin API key, con CORS).
// Toma la ubicación del navegador; si no da permiso, usa un fallback (NE Argentina).

import React, { useEffect, useState } from 'react';

interface WeatherData {
  temp: number;
  code: number;
}

// Mapeo de códigos WMO a etiqueta + emoji
const WMO: Record<number, { label: string; emoji: string }> = {
  0: { label: 'Despejado', emoji: '☀️' },
  1: { label: 'Mayormente despejado', emoji: '🌤️' },
  2: { label: 'Parcialmente nublado', emoji: '⛅' },
  3: { label: 'Nublado', emoji: '☁️' },
  45: { label: 'Niebla', emoji: '🌫️' },
  48: { label: 'Niebla', emoji: '🌫️' },
  51: { label: 'Llovizna', emoji: '🌦️' },
  53: { label: 'Llovizna', emoji: '🌦️' },
  55: { label: 'Llovizna', emoji: '🌦️' },
  61: { label: 'Lluvia', emoji: '🌧️' },
  63: { label: 'Lluvia', emoji: '🌧️' },
  65: { label: 'Lluvia fuerte', emoji: '🌧️' },
  71: { label: 'Nieve', emoji: '🌨️' },
  73: { label: 'Nieve', emoji: '🌨️' },
  75: { label: 'Nieve', emoji: '🌨️' },
  80: { label: 'Chaparrones', emoji: '🌦️' },
  81: { label: 'Chaparrones', emoji: '🌦️' },
  82: { label: 'Chaparrones fuertes', emoji: '⛈️' },
  95: { label: 'Tormenta', emoji: '⛈️' },
  96: { label: 'Tormenta', emoji: '⛈️' },
  99: { label: 'Tormenta', emoji: '⛈️' },
};

const WeatherWidget: React.FC<{ className?: string }> = ({ className = '' }) => {
  const [weather, setWeather] = useState<WeatherData | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchWeather = async (lat: number, lon: number) => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,weather_code&timezone=auto`
        );
        const data = await res.json();
        if (!cancelled && data?.current) {
          setWeather({
            temp: Math.round(data.current.temperature_2m),
            code: data.current.weather_code,
          });
        }
      } catch {
        /* si falla, simplemente no mostramos el widget */
      }
    };

    // Fallback: Corrientes / Resistencia (NE Argentina)
    const fallback = () => fetchWeather(-27.47, -58.83);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => fetchWeather(pos.coords.latitude, pos.coords.longitude),
        () => fallback(),
        { timeout: 8000, maximumAge: 10 * 60 * 1000 }
      );
    } else {
      fallback();
    }

    return () => { cancelled = true; };
  }, []);

  if (!weather) return null;

  const info = WMO[weather.code] || { label: 'Clima', emoji: '🌡️' };

  return (
    <div
      className={`items-center gap-2 px-3 py-1.5 rounded-xl bg-gradient-to-br from-sky-50 to-blue-50 border border-sky-100 ${className}`}
      title={`${info.label} · ${weather.temp}°C`}
    >
      <span className="text-xl leading-none">{info.emoji}</span>
      <div className="leading-tight">
        <p className="text-sm font-bold text-gray-800">{weather.temp}°C</p>
        <p className="text-[10px] text-gray-500 -mt-0.5">{info.label}</p>
      </div>
    </div>
  );
};

export default WeatherWidget;
